import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, StyledButton, AlertBox } from '../main/CommonStyles';
import apiClient from '../services/api';
import { Loader2, AlertTriangle, Eye, User, CheckCircle } from 'lucide-react';

const ProfilesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
`;

const ProfileCard = styled(Card)`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${({ theme }) => theme.spacing.md};

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
    }
`;

const ProfileInfo = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex-grow: 1;

    img {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
    }

    .placeholder-avatar {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.colors.neutralLight};
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .details {
        font-size: 0.9rem;
        color: ${({ theme }) => theme.colors.textMedium};
        strong {
            display: block;
            font-size: 1.1rem;
            font-weight: 600;
            color: ${({ theme }) => theme.colors.textDark};
            margin-bottom: 0.25rem;
        }
    }
`;

const Actions = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.sm};
    flex-shrink: 0;
`;

const PendingProfiles = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [approvingId, setApprovingId] = useState(null); // NOWOŚĆ: Stan do śledzenia, który profil jest akceptowany
    const navigate = useNavigate();

    const fetchPendingProfiles = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await apiClient.get('/admin/psychologists/pending');
            setProfiles(response.data);
        } catch (err) {
            setError('Nie udało się pobrać profili do akceptacji.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPendingProfiles();
    }, [fetchPendingProfiles]);

    const handleViewProfile = (profileId) => {
        navigate(`/app/psychologist/${profileId}`);
    };

    // NOWOŚĆ: Funkcja do akceptowania profilu bezpośrednio z listy
    const handleApprove = async (profileId) => {
        setApprovingId(profileId); // Ustawia ładowanie dla konkretnego przycisku
        try {
            await apiClient.post(`/admin/psychologists/${profileId}/approve`);
            // Usuwa zaakceptowany profil z listy w UI
            setProfiles(prev => prev.filter(p => p.id !== profileId));
        } catch (err) {
            alert('Wystąpił błąd podczas akceptowania profilu.');
        } finally {
            setApprovingId(null); // Kończy ładowanie
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center' }}><Loader2 className="icon-spin" /> Ładowanie...</div>;
    }

    if (error) {
        return <AlertBox type="error"><AlertTriangle /> {error}</AlertBox>;
    }

    return (
        <div>
            <PageHeader>Profile Oczekujące na Akceptację</PageHeader>
            <PageDescription>
                Przejrzyj i zaakceptuj profile psychologów. Po akceptacji profil stanie się widoczny publicznie.
            </PageDescription>
            <ProfilesList>
                {profiles.length > 0 ? (
                    profiles.map(profile => (
                        <ProfileCard key={profile.id}>
                            <ProfileInfo>
                                {profile.userImageUrl ? (
                                    <img src={profile.userImageUrl} alt={profile.username} />
                                ) : (
                                    <div className="placeholder-avatar"><User size={24} color="#94a3b8"/></div>
                                )}
                                <div className="details">
                                    <strong>{`${profile.firstName} ${profile.lastName}`}</strong>
                                    <span>{profile.professionalTitle} (@{profile.username})</span>
                                </div>
                            </ProfileInfo>
                            {/* ZMIANA: Dwa osobne przyciski */}
                            <Actions>
                                <StyledButton $variant="outline" onClick={() => handleViewProfile(profile.id)}>
                                    <Eye size={16} /> Podgląd
                                </StyledButton>
                                <StyledButton
                                    $variant="secondary"
                                    onClick={() => handleApprove(profile.id)}
                                    disabled={approvingId === profile.id}
                                >
                                    {approvingId === profile.id ? (
                                        <Loader2 size={16} className="icon-spin" />
                                    ) : (
                                        <CheckCircle size={16} />
                                    )}
                                    {approvingId === profile.id ? 'Akceptuję...' : 'Zatwierdź'}
                                </StyledButton>
                            </Actions>
                        </ProfileCard>
                    ))
                ) : (
                    <p>Brak profili oczekujących na akceptację.</p>
                )}
            </ProfilesList>
        </div>
    );
};

export default PendingProfiles;